import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AssessmentWizard from '../components/AssessmentWizard';
import { ClipboardList, Plus, User, Calendar, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// ── Konfiguration per flik ─────────────────────────────────────────────────────

const TABS = [
  { key: 'Utkast',            label: 'Utkast',             statuses: ['Utkast'] },
  { key: 'Pagaende',          label: 'Pågående',           statuses: ['Pagaende_bedomning', 'Revideras'] },
  { key: 'Faststalld',        label: 'Fastställda',        statuses: ['Faststalld'] },
  { key: 'Under_uppfoljning', label: 'Under uppföljning',  statuses: ['Under_uppfoljning'] },
  { key: 'Avslutade',         label: 'Avslutade',          statuses: ['Avslutad', 'Arkiverad'] },
];

const STATUS_LABELS = {
  Utkast: 'Utkast',
  Pagaende_bedomning: 'Pågående',
  Faststalld: 'Fastställd',
  Under_uppfoljning: 'Under uppföljning',
  Revideras: 'Revideras',
  Avslutad: 'Avslutad',
  Arkiverad: 'Arkiverad',
};

const STATUS_COLORS = {
  Utkast: 'bg-gray-100 text-gray-600',
  Pagaende_bedomning: 'bg-blue-100 text-blue-700',
  Faststalld: 'bg-green-100 text-green-700',
  Under_uppfoljning: 'bg-purple-100 text-purple-700',
  Revideras: 'bg-orange-100 text-orange-700',
  Avslutad: 'bg-gray-200 text-gray-500',
  Arkiverad: 'bg-gray-200 text-gray-500',
};

const TYPE_LABELS = {
  Inledande_kartlaggning: 'Inledande',
  Uppfoljande_kartlaggning: 'Uppföljande',
  Revidering: 'Revidering',
  Infor_ny_insats: 'Inför ny insats',
  Infor_avslut: 'Inför avslut',
};

function participantName(participants, id) {
  const p = participants.find((x) => x.id === id);
  if (!p) return id;
  return [p.first_name, p.last_name].filter(Boolean).join(' ') || p.name || id;
}

// ══════════════════════════════════════════════════════════════════════════════

const AssessmentsPage = () => {
  const { user } = useAuth();

  const [assessments, setAssessments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Utkast');

  // Filter
  const [filterParticipant, setFilterParticipant] = useState('');

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const isWriter = ['super_admin', 'customer_admin', 'employee'].includes(user?.role);

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, pRes] = await Promise.all([
          api.get('/org/assessments'),
          api.get('/participants'),
        ]);
        setAssessments(aRes.data || []);
        setParticipants(pRes.data || []);
      } catch {
        toast.error('Kunde inte ladda kartläggningar');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeStatuses = TABS.find((t) => t.key === activeTab)?.statuses || [];

  const filtered = assessments.filter((a) => {
    if (!activeStatuses.includes(a.status)) return false;
    if (filterParticipant && a.participant_id !== filterParticipant) return false;
    return true;
  });

  const countForTab = (tab) => {
    const statuses = TABS.find((t) => t.key === tab)?.statuses || [];
    return assessments.filter((a) => statuses.includes(a.status)).length;
  };

  const handleWizardSaved = (saved) => {
    setAssessments((prev) => {
      const exists = prev.find((a) => a.id === saved.id);
      return exists
        ? prev.map((a) => a.id === saved.id ? { ...a, ...saved } : a)
        : [saved, ...prev];
    });
    setWizardOpen(false);
    setEditId(null);
  };

  const openNew = () => {
    setEditId(null);
    setWizardOpen(true);
  };

  const openEdit = (id) => {
    setEditId(id);
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    setEditId(null);
    // Reload to pick up autosave changes
    api.get('/org/assessments').then((r) => setAssessments(r.data || [])).catch(() => {});
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="assessments-page">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Kartläggningar</h1>
            <p className="text-muted-foreground mt-2">Systematisk bedömning av deltagarnas förutsättningar</p>
          </div>
          {isWriter && (
            <Button
              data-testid="new-assessment-btn"
              className="rounded-full font-bold"
              onClick={openNew}
            >
              <Plus className="mr-2" size={18} />
              Ny kartläggning
            </Button>
          )}
        </div>

        {/* ── Filter ──────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <select
            data-testid="filter-participant"
            value={filterParticipant}
            onChange={(e) => setFilterParticipant(e.target.value)}
            className="h-9 px-3 border rounded-md bg-background text-sm border-input focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Alla deltagare</option>
            {participants.map((p) => (
              <option key={p.id} value={p.id}>{participantName(participants, p.id)}</option>
            ))}
          </select>
          {filterParticipant && (
            <Button variant="ghost" size="sm" onClick={() => setFilterParticipant('')} className="text-xs rounded-full">
              <RefreshCw size={12} className="mr-1" /> Rensa filter
            </Button>
          )}
        </div>

        {/* ── Flikar ──────────────────────────────────────────────────────── */}
        <div className="flex gap-1 border-b" data-testid="tab-bar">
          {TABS.map((tab) => {
            const count = countForTab(tab.key);
            return (
              <button
                key={tab.key}
                data-testid={`tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-primary text-foreground font-bold'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
                {count > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs bg-muted font-bold">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Lista ───────────────────────────────────────────────────────── */}
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Laddar kartläggningar...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center" data-testid="empty-state">
            <ClipboardList size={40} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-lg">
              {assessments.length === 0
                ? 'Inga kartläggningar ännu. Klicka "Ny kartläggning" för att börja.'
                : 'Inga kartläggningar i den här fliken.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((a) => (
              <Card
                key={a.id}
                data-testid={`assessment-card-${a.id}`}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openEdit(a.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABELS[a.status] || a.status}
                        </span>
                        <span className="text-xs text-muted-foreground font-medium">
                          {TYPE_LABELS[a.assessment_type] || a.assessment_type}
                        </span>
                        {a.version_number > 1 && (
                          <span className="text-xs text-muted-foreground">v{a.version_number}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <User size={13} className="text-muted-foreground shrink-0" />
                        <span className="font-medium">{participantName(participants, a.participant_id)}</span>
                      </div>

                      {a.purpose && (
                        <p className="text-sm text-muted-foreground line-clamp-1">{a.purpose}</p>
                      )}
                    </div>

                    <div className="text-right shrink-0 space-y-1">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground justify-end">
                        <Calendar size={12} />
                        <span>{a.start_date}</span>
                      </div>
                      {a.entity_name && (
                        <p className="text-xs text-muted-foreground">{a.entity_name}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Wizard ──────────────────────────────────────────────────────────── */}
      {wizardOpen && (
        <AssessmentWizard
          assessmentId={editId}
          onClose={closeWizard}
          onSaved={handleWizardSaved}
        />
      )}
    </DashboardLayout>
  );
};

export default AssessmentsPage;
